import { initializeApp } from "firebase/app";
import { getAuth } from "firebase/auth";
import { initializeFirestore, doc, getDocFromServer, enableIndexedDbPersistence } from "firebase/firestore";
import firebaseConfig from "../../firebase-applet-config.json";

const app = initializeApp(firebaseConfig);

// Initialize Firestore with settings optimized for environment constraints
export const db = initializeFirestore(app, {
  experimentalForceLongPolling: true,
}, firebaseConfig.firestoreDatabaseId);

// Attempt to enable persistence for offline-first experience
try {
  enableIndexedDbPersistence(db).catch((err) => {
    if (err.code === 'failed-precondition') {
      console.warn("Firestore persistence failed: Multiple tabs open.");
    } else if (err.code === 'unimplemented') {
      console.warn("Firestore persistence not supported by browser.");
    }
  });
} catch (e) {
  console.warn("Persistence initialization error", e);
}

// Check for long-term connectivity issues
let connectivityWarningTimeout: any = null;
const CHECK_INTERVAL = 15000; // 15 seconds

export function monitorConnection(onOffline: () => void) {
  const checkConnection = async () => {
    try {
      // If we get ANY response from the server, clear the offline warning
      await getDocFromServer(doc(db, "test", "connection"));
      
      if (connectivityWarningTimeout) {
        console.log("Connection restored or confirmed.");
        clearTimeout(connectivityWarningTimeout);
        connectivityWarningTimeout = null;
      }
    } catch (error: any) {
      const isOfflineError = error?.code === 'unavailable' || 
                           (error instanceof Error && error.message.includes("client is offline")) ||
                           error?.code === 'deadline-exceeded';

      if (isOfflineError) {
        if (!connectivityWarningTimeout) {
          console.warn("Connection appears slow or offline. Setting warning timeout.");
          connectivityWarningTimeout = setTimeout(onOffline, CHECK_INTERVAL);
        }
      } else {
        // If it's a permission-denied or not-found, it still counts as "connected"
        if (connectivityWarningTimeout) {
          clearTimeout(connectivityWarningTimeout);
          connectivityWarningTimeout = null;
        }
      }
    }
  };
  
  const interval = setInterval(checkConnection, CHECK_INTERVAL * 2);
  checkConnection(); // Initial check
  
  return () => {
    clearInterval(interval);
    if (connectivityWarningTimeout) clearTimeout(connectivityWarningTimeout);
  };
}

export const auth = getAuth(app);

export enum OperationType {
  CREATE = 'create',
  UPDATE = 'update',
  DELETE = 'delete',
  LIST = 'list',
  GET = 'get',
  WRITE = 'write',
}

export interface FirestoreErrorInfo {
  error: string;
  operationType: OperationType;
  path: string | null;
  authInfo: {
    userId?: string | null;
    email?: string | null;
    emailVerified?: boolean | null;
    isAnonymous?: boolean | null;
    tenantId?: string | null;
    providerInfo?: {
      providerId?: string | null;
      email?: string | null;
    }[];
  }
}

export function handleFirestoreError(error: unknown, operationType: OperationType, path: string | null) {
  const errInfo: FirestoreErrorInfo = {
    error: error instanceof Error ? error.message : String(error),
    authInfo: {
      userId: auth.currentUser?.uid,
      email: auth.currentUser?.email,
      emailVerified: auth.currentUser?.emailVerified,
      isAnonymous: auth.currentUser?.isAnonymous,
      tenantId: auth.currentUser?.tenantId,
      providerInfo: auth.currentUser?.providerData?.map(provider => ({
        providerId: provider.providerId,
        email: provider.email,
      })) || []
    },
    operationType,
    path
  }
  console.error('Firestore Error: ', JSON.stringify(errInfo));
  throw new Error(JSON.stringify(errInfo));
}

// Test connection on boot
async function testConnection() {
  try {
    await getDocFromServer(doc(db, "test", "connection"));
  } catch (error: any) {
    if (error instanceof Error && error.message.includes("the client is offline")) {
      console.error("Please check your Firebase configuration.");
    } else if (error?.code === 'permission-denied' || error?.message?.includes('Missing or insufficient permissions')) {
      // Ignored for test connection as it's meant to fail securely
    }
  }
}
testConnection();
